import { styled } from '@linaria/react';
import { Collapse, Group, Select, Stack } from '@mantine/core';
import { DatePickerInput, type DateValue } from '@mantine/dates';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { safeParseDateInput } from '../../../helpers/date-time-helpers';
import { useSearchParams } from 'react-router';
import { deduplicateComboboxGroups } from '../../../helpers/general-helpers';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { newCOLORS } from '../../../styles/colors';
import { inkLight } from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Radio } from '../../../ui-library/radio/radio';
import { Text } from '../../../ui-library/typography/typography';
import { getBoardOptions, useTeams, useWorkPeriodsData } from '../hooks/work-periods.hooks';
import type { DateRange } from '../work-periods.type';
import { WorkPeriodType } from '../work-periods.type';

/**
 * WorkPeriodsFilter component
 * Allows users to filter work periods by board, type (defined or custom), and date range
 * @param {boolean} isComparisonView - Whether this component is used in the comparison view
 */
export function WorkPeriodsFilter({ isComparisonView = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Use the new work period store
  const {
    selectedBoardId,
    selectedWorkPeriodType,
    dateRange,
    selectedWorkPeriods,
    setSelectedBoardId,
    setSelectedWorkPeriodType,
    setSelectedWorkPeriods,
    setDateRange,
    clearWorkPeriods,
  } = useWorkPeriodStore();

  // Fetch data independently
  const teams = useTeams();
  const { data: workPeriods } = useWorkPeriodsData(selectedBoardId);

  // Format boards from teams data
  const formattedBoards = useMemo(() => {
    const boardOptions = getBoardOptions(teams);
    const deduplicated = deduplicateComboboxGroups(boardOptions);
    return deduplicated.flatMap((group) =>
      group.items.map((item) => {
        const comboItem = typeof item === 'string' ? { value: item, label: item } : item;
        return {
          value: comboItem.value as string,
          label: comboItem.label as string,
        };
      }),
    );
  }, [teams]);

  const [pendingWorkPeriodIds, setPendingWorkPeriodIds] = useState<string[] | null>(null);
  const [didInitFromUrl, setDidInitFromUrl] = useState(false);
  const [shouldAutoSelectFromUrl, setShouldAutoSelectFromUrl] = useState(false);

  // Auto-select a default board when available
  useEffect(() => {
    if (selectedWorkPeriodType === WorkPeriodType.Defined && formattedBoards.length > 0) {
      const urlBoardId = searchParams.get('workPeriodBoardId');
      const urlBoardIsValid = urlBoardId && formattedBoards.some((board) => board.value === urlBoardId);

      if (urlBoardIsValid && selectedBoardId !== urlBoardId) {
        setSelectedBoardId(urlBoardId);
        clearWorkPeriods();
        return;
      }

      const isCurrentBoardValid = selectedBoardId && formattedBoards.some((board) => board.value === selectedBoardId);

      if (!selectedBoardId || !isCurrentBoardValid) {
        const defaultBoard = formattedBoards[0];
        if (defaultBoard) {
          setSelectedBoardId(defaultBoard.value);
        }
      }
    }
  }, [selectedWorkPeriodType, selectedBoardId, formattedBoards, setSelectedBoardId, searchParams, clearWorkPeriods]);

  // One-time URL sync for work period filters
  useEffect(() => {
    if (didInitFromUrl) return;

    const urlBoardId = searchParams.get('workPeriodBoardId');
    const urlStartDate = searchParams.get('startDate');
    const urlEndDate = searchParams.get('endDate');
    const urlSelectedWorkPeriodId = searchParams.get('selectedWorkPeriodId');
    const urlSelectedWorkPeriodIds = searchParams.get('selectedWorkPeriodIds');

    const hasAnyUrlParams = Boolean(
      urlBoardId || urlStartDate || urlEndDate || urlSelectedWorkPeriodId || urlSelectedWorkPeriodIds,
    );
    if (!hasAnyUrlParams) {
      setDidInitFromUrl(true);
      return;
    }

    setSelectedWorkPeriodType(WorkPeriodType.Defined);

    if (urlStartDate && urlEndDate) {
      setDateRange({ startDate: new Date(urlStartDate), endDate: new Date(urlEndDate) });
      setShouldAutoSelectFromUrl(true);
    }

    // Support both single ID (legacy) and comma-separated IDs
    if (urlSelectedWorkPeriodIds) {
      const ids = urlSelectedWorkPeriodIds.split(',').filter(Boolean);
      if (ids.length > 0) {
        setPendingWorkPeriodIds(ids);
        setShouldAutoSelectFromUrl(true);
      }
    } else if (urlSelectedWorkPeriodId) {
      setPendingWorkPeriodIds([urlSelectedWorkPeriodId]);
      setShouldAutoSelectFromUrl(true);
    }

    setDidInitFromUrl(true);
  }, [didInitFromUrl, searchParams, setDateRange, setSelectedWorkPeriodType]);

  // Apply pending sprint selection once the work periods list is loaded, then clear URL params
  useEffect(() => {
    if (!workPeriods || workPeriods.length === 0) return;

    // Explicit selection from URL (exact match by ID(s))
    if (pendingWorkPeriodIds && pendingWorkPeriodIds.length > 0) {
      // Wait for board ID to be set from URL before selecting work period
      if (!selectedBoardId) return;

      const pendingSet = new Set(pendingWorkPeriodIds);
      const matched = workPeriods.filter((wp) => pendingSet.has(wp.id));

      if (matched.length === 0) return;

      // Sort by start_date descending and cap at 7 (the comparison view limit)
      const sorted = [...matched].sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
      setSelectedWorkPeriods(sorted.slice(0, 7));

      setPendingWorkPeriodIds(null);
      setSearchParams(new URLSearchParams(), { replace: true });
      return;
    }

    // If coming from dashboard widget and no explicit selection, auto-select the latest work period by date.
    if (shouldAutoSelectFromUrl && selectedWorkPeriods.length === 0) {
      if (!selectedBoardId) return;

      const latest = workPeriods.reduce<(typeof workPeriods)[number] | null>((latestSoFar, workPeriod) => {
        if (!latestSoFar) return workPeriod;
        const workPeriodTime = new Date(workPeriod.end_date ?? workPeriod.start_date).getTime();
        const latestTime = new Date(latestSoFar.end_date ?? latestSoFar.start_date).getTime();
        return workPeriodTime >= latestTime ? workPeriod : latestSoFar;
      }, null);

      if (latest) {
        setSelectedWorkPeriods([latest]);
      }

      setShouldAutoSelectFromUrl(false);
      setSearchParams(new URLSearchParams(), { replace: true });
    }
  }, [
    selectedBoardId,
    pendingWorkPeriodIds,
    selectedWorkPeriods.length,
    setSearchParams,
    setSelectedWorkPeriods,
    shouldAutoSelectFromUrl,
    workPeriods,
  ]);

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Handle board selection
  const handleBoardSelect = useCallback(
    (boardId: string | null) => {
      setSelectedBoardId(boardId);
      clearWorkPeriods();
    },
    [setSelectedBoardId, clearWorkPeriods],
  );

  // Handle work period type selection
  const handleWorkPeriodTypeSelect = useCallback(
    (type: WorkPeriodType) => {
      setSelectedWorkPeriodType(type);
      clearWorkPeriods();
    },
    [setSelectedWorkPeriodType, clearWorkPeriods],
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (range: DateRange) => {
      setDateRange(range);
    },
    [setDateRange],
  );

  const onDateRangeChange = useCallback(
    (field: keyof DateRange, value: DateValue) => {
      const parsedDate = value ? (safeParseDateInput(value)?.toDate() ?? null) : null;
      const newDateRange = { ...dateRange, [field]: parsedDate };
      handleDateRangeChange(newDateRange);
    },
    [dateRange, handleDateRangeChange],
  );

  // Handle work period selection for radio buttons (single selection) and checkboxes (multi-select)
  const onWorkPeriodSelect = useCallback(
    (workPeriodId: string, isMultiSelect = false) => {
      const workPeriod = workPeriods?.find((wp: { id: string }) => wp.id === workPeriodId);
      if (workPeriod) {
        if (isMultiSelect) {
          // Check if work period is already selected
          const isSelected = selectedWorkPeriods.some((wp) => wp.id === workPeriodId);

          if (isSelected) {
            // Remove the work period from selection
            setSelectedWorkPeriods(selectedWorkPeriods.filter((wp) => wp.id !== workPeriodId));
          } else {
            // Add the work period to selection (with limit of 7)
            if (selectedWorkPeriods.length >= 7) {
              setSelectedWorkPeriods([...selectedWorkPeriods.slice(1), workPeriod]);
            } else {
              setSelectedWorkPeriods([...selectedWorkPeriods, workPeriod]);
            }
          }
        } else {
          setSelectedWorkPeriods([workPeriod]);
        }
      }
    },
    [workPeriods, setSelectedWorkPeriods, selectedWorkPeriods],
  );

  // helper function to determine if a checkbox should be disabled
  const isCheckboxDisabled = useCallback(
    (workPeriodId: string) => {
      // If this work period is already selected, never disable (allow unchecking)
      if (selectedWorkPeriods.some((wp) => wp.id === workPeriodId)) {
        return false;
      }

      // If we already have 7 selections, disable unselected checkboxes
      return selectedWorkPeriods.length >= 7;
    },
    [selectedWorkPeriods],
  );

  // Get the currently selected work period ID for radio buttons
  const selectedWorkPeriodId = selectedWorkPeriods.length > 0 ? selectedWorkPeriods[0].id : '';

  // Helper function to check if a work period is selected for checkboxes
  const isWorkPeriodSelected = useCallback(
    (workPeriodId: string) => {
      return selectedWorkPeriods.some((wp) => wp.id === workPeriodId);
    },
    [selectedWorkPeriods],
  );

  // Helper function to format dates
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <FilterContainer>
      <Select
        data={formattedBoards}
        value={
          selectedBoardId && formattedBoards.some((board) => board.value === selectedBoardId) ? selectedBoardId : null
        }
        onChange={(value: string | null) => handleBoardSelect(value)}
        radius="xl"
        placeholder="Select a board"
        searchable
        clearable={false}
        allowDeselect={false}
      />

      {selectedBoardId && formattedBoards.length > 0 && (
        <>
          {!isComparisonView ? (
            <Select
              data={[
                { value: WorkPeriodType.Defined, label: 'Pre-defined' },
                { value: WorkPeriodType.Custom, label: 'Ad-hoc' },
              ]}
              value={selectedWorkPeriodType}
              onChange={(value) => handleWorkPeriodTypeSelect(value as WorkPeriodType)}
              radius="xl"
              style={{ marginTop: 4 }}
              clearable={false}
              allowDeselect={false}
            />
          ) : null}

          <Divider style={{ marginTop: 16 }} />

          {selectedWorkPeriodType === WorkPeriodType.Defined && workPeriods && workPeriods.length > 0 && (
            <WorkPeriodsContainer>
              <Group justify="space-between" mt={16} mb={16}>
                <Group gap="xs">
                  <Icon name="calendar_month" size={20} color={inkLight} />
                  <Text weight="bold">Work Period{isComparisonView ? 's' : ''}</Text>
                </Group>
                <Icon
                  name={isCollapsed ? 'unfold_more' : 'unfold_less'}
                  size={20}
                  color={inkLight}
                  onClick={() => setIsCollapsed(!isCollapsed)}
                />
              </Group>
              <Collapse expanded={!isCollapsed}>
                {isComparisonView ? (
                  // Radio group for comparison view (multi-select behavior)
                  <Stack gap={16}>
                    {workPeriods
                      .sort((a, b) => dayjs(b.start_date).diff(dayjs(a.start_date)))
                      .map((workPeriod) => (
                        <WorkPeriodItem
                          key={workPeriod.id}
                          onClick={() => !isCheckboxDisabled(workPeriod.id) && onWorkPeriodSelect(workPeriod.id, true)}
                          style={{
                            cursor: isCheckboxDisabled(workPeriod.id) ? 'not-allowed' : 'pointer',
                            opacity: isCheckboxDisabled(workPeriod.id) ? 0.6 : 1,
                          }}
                        >
                          <Radio
                            value={workPeriod.id}
                            checked={isWorkPeriodSelected(workPeriod.id)}
                            disabled={isCheckboxDisabled(workPeriod.id)}
                            onChange={() => {}} // Prevent default onChange, use onClick instead
                            label={
                              <LabelContainer style={{ cursor: 'pointer' }}>
                                <WorkPeriodName>{workPeriod.name}</WorkPeriodName>
                                <DateRangeText>
                                  {formatDate(workPeriod.start_date)} - {formatDate(workPeriod.end_date)}
                                </DateRangeText>
                              </LabelContainer>
                            }
                            styles={{
                              label: {
                                width: '100%',
                                pointerEvents: 'none', // Prevent double clicking
                              },
                              radio: {
                                pointerEvents: 'none', // Prevent radio input from handling clicks
                              },
                            }}
                          />
                        </WorkPeriodItem>
                      ))}
                  </Stack>
                ) : (
                  // Radio group for assessment view (single-select)
                  <Stack gap={16}>
                    {workPeriods
                      .sort((a, b) => dayjs(b.start_date).diff(dayjs(a.start_date)))
                      .map((workPeriod) => (
                        <WorkPeriodItem key={workPeriod.id}>
                          <Radio
                            value={workPeriod.id}
                            checked={selectedWorkPeriodId === workPeriod.id}
                            onChange={() => onWorkPeriodSelect(workPeriod.id)}
                            aria-checked={selectedWorkPeriodId === workPeriod.id}
                            label={
                              <LabelContainer style={{ cursor: 'pointer' }}>
                                <WorkPeriodName>{workPeriod.name}</WorkPeriodName>
                                <DateRangeText>
                                  {formatDate(workPeriod.start_date)} - {formatDate(workPeriod.end_date)}
                                </DateRangeText>
                              </LabelContainer>
                            }
                            styles={{
                              label: {
                                width: '100%',
                              },
                            }}
                          />
                        </WorkPeriodItem>
                      ))}
                  </Stack>
                )}
              </Collapse>
            </WorkPeriodsContainer>
          )}

          {selectedWorkPeriodType === WorkPeriodType.Custom && (
            <>
              <DatePickerInput
                label="Start Date"
                placeholder="Select start date"
                value={dateRange?.startDate}
                onChange={(value) => onDateRangeChange('startDate', value)}
                maxDate={dateRange?.endDate || undefined}
                leftSection={<Icon name="calendar_month" size={16} color={newCOLORS.blue} />}
                leftSectionPointerEvents="none"
                popoverProps={{ zIndex: 1000, position: 'bottom-start', middlewares: { flip: false, shift: false } }}
              />

              <DatePickerInput
                label="End Date"
                placeholder="Select end date"
                value={dateRange?.endDate}
                onChange={(value) => onDateRangeChange('endDate', value)}
                minDate={dateRange?.startDate || undefined}
                leftSection={<Icon name="calendar_month" size={16} color={newCOLORS.blue} />}
                leftSectionPointerEvents="none"
                popoverProps={{ zIndex: 1000, position: 'bottom-start', middlewares: { flip: false, shift: false } }}
              />
            </>
          )}
        </>
      )}
    </FilterContainer>
  );
}

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WorkPeriodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

`;

const WorkPeriodItem = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  cursor: pointer;
`;

const WorkPeriodName = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const DateRangeText = styled.span`
  font-size: 12px;
  color: ${newCOLORS.darkGray};
`;
